/*
 * Uploads from the browser (ADR 0009 §6): what each place takes, as the server checks it and the
 * pages tell it before sending anything. The browser sends a file straight to the imports bucket,
 * in parts, with addresses the server signs (each with the part's size, so nothing larger fits);
 * once the malware scan finds it clean, the server checks its type by its bytes and copies it into
 * the files bucket. Read by the editor's pages and the wizard's (by the subpath
 * `@eklevo/shared/uploads` in the back office) and by the server (packages/core,
 * services/uploads.ts).
 */

/** The places a page uploads a file to. */
export const UPLOAD_SLOTS = ['video', 'captions', 'material', 'logo'] as const
export type UploadSlot = (typeof UPLOAD_SLOTS)[number]

/** The places of a lesson: what the course editor uploads (the purpose of the file's row). */
export const LESSON_UPLOAD_SLOTS = ['video', 'captions', 'material'] as const
export type LessonUploadSlot = (typeof LESSON_UPLOAD_SLOTS)[number]

const MB = 1024 * 1024

export interface UploadRule {
  /** The most it takes, in bytes. */
  maxBytes: number
  /** The types the browser may declare; the server checks the bytes anyway. */
  types: readonly string[]
  /** For the page's file picker. */
  accept: string
}

/**
 * What each place takes: a lesson's video as MP4 with H.264 up to 500 MB, the only format every
 * browser plays without transcoding (ADR 0009, out of the first version: transcoding); its
 * captions as WebVTT; a material as PDF; and an organization's logo as PNG or JPEG up to 1 MB
 * (ADR 0007). The same limits as the course folders the operator tool imports.
 */
export const UPLOAD_RULES: Record<UploadSlot, UploadRule> = {
  video: { maxBytes: 500 * MB, types: ['video/mp4'], accept: '.mp4,video/mp4' },
  captions: { maxBytes: 2 * MB, types: ['text/vtt'], accept: '.vtt,text/vtt' },
  material: { maxBytes: 50 * MB, types: ['application/pdf'], accept: '.pdf,application/pdf' },
  logo: {
    maxBytes: 1 * MB,
    types: ['image/png', 'image/jpeg'],
    accept: '.png,.jpg,.jpeg,image/png,image/jpeg',
  },
}

/**
 * The size of each part of an upload but the last (S3 takes 5 MiB or more; the last, or the only
 * one of a small file, any size). Every upload goes in parts: the server completes it, once.
 */
export const UPLOAD_PART_BYTES = 10 * MB

/**
 * What an organization's HR may upload in a calendar month of the organization's time zone, in
 * bytes: 20 GB [T] (ADR 0009 §6). Eklevo's catalogue does not count.
 */
export const MONTHLY_UPLOAD_BYTES = 20 * 1024 * MB

/**
 * Why an upload is refused, as the page says it at its field:
 * - `too-large`, `empty`, `wrong-type`: what the page declared, or the bytes once scanned (a PDF
 *   renamed .mp4, a video that is not H.264);
 * - `wrong-dimensions`: a logo smaller than 32 px or larger than 4000 px a side (ADR 0007);
 * - `quota-exceeded`: the organization's monthly allowance would be passed;
 * - `infected`: the malware scan found a threat; `scan-failed`: it could not scan it;
 * - `missing`: nothing arrived (the browser did not finish sending it);
 * - `expired`: the upload's permission ran out (a day) or is not this session's to finish.
 */
export const UPLOAD_REFUSALS = [
  'too-large',
  'empty',
  'wrong-type',
  'wrong-dimensions',
  'quota-exceeded',
  'infected',
  'scan-failed',
  'missing',
  'expired',
] as const
export type UploadRefusal = (typeof UPLOAD_REFUSALS)[number]

/** The type a page declares for a file: the browser's, or the one its extension names. */
export function declaredType(slot: UploadSlot, file: { name: string; type: string }): string {
  const rule = UPLOAD_RULES[slot]
  if (rule.types.includes(file.type)) return file.type
  const extension = file.name.toLowerCase().split('.').at(-1) ?? ''
  const byExtension: Record<string, string> = {
    mp4: 'video/mp4',
    vtt: 'text/vtt',
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
  }
  return byExtension[extension] ?? file.type
}

/**
 * What the page tells before sending a file: too large, empty, or not of a type the place takes
 * (by what the browser says and the extension; the server checks the bytes). Null when it may go.
 */
export function uploadProblem(
  slot: UploadSlot,
  file: { name: string; type: string; size: number },
): Extract<UploadRefusal, 'too-large' | 'empty' | 'wrong-type'> | null {
  const rule = UPLOAD_RULES[slot]
  if (!rule.types.includes(declaredType(slot, file))) return 'wrong-type'
  if (file.size === 0) return 'empty'
  if (file.size > rule.maxBytes) return 'too-large'
  return null
}
